import { useState, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, MessageSquare, Copy, Check, Download, Loader2 } from 'lucide-react';
import CitationLink from './CitationLink';
import FuentesCitadas from './FuentesCitadas';
import { Citation } from '../../services/chatbot.service';
import { linkifyNorms } from '../../utils/legalNorms';
import { buildAnswerPlainText } from '../../utils/exportText';

interface ChatBubbleProps {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  onDocumentClick: (citation: Citation, messageCitations: Citation[]) => void;
  onSuggestionClick?: (suggestion: string) => void;
  isStreaming?: boolean;
  /** The user's question that produced this answer — used as the DOCX
   *  export title. Optional: falls back to "Consulta AlertAI" when absent. */
  question?: string;
}

const MAX_SUGGESTIONS = 3;

/**
 * Extract follow-up suggestions from the response content.
 */
function extractSuggestions(text: string): { mainContent: string; suggestions: string[] } {
  const marker = /\*?\*?Preguntas relacionadas:?\*?\*?\s*\n/i;
  const match = text.match(marker);
  if (!match || match.index === undefined) {
    return { mainContent: text, suggestions: [] };
  }

  const mainContent = text.substring(0, match.index).trimEnd();
  const suggestionsBlock = text.substring(match.index + match[0].length);

  const suggestions = suggestionsBlock
    .split('\n')
    .map(line => line.replace(/^[\s]*[-*•]\s*/, '').replace(/^\d+\.\s*/, '').trim())
    .filter(line => line.length > 10 && !line.toLowerCase().includes('esta informacion es referencial'));

  return { mainContent, suggestions };
}

export default function ChatBubble({
  id, role, content, citations = [],
  onDocumentClick, onSuggestionClick, isStreaming, question
}: ChatBubbleProps) {
  const [copied, setCopied] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);

  // Extract suggestions
  const { mainContent, suggestions } = useMemo(
    () => isStreaming ? { mainContent: content, suggestions: [] } : extractSuggestions(content),
    [content, isStreaming]
  );

  // Build a lookup of citation uuid -> {citation, index}, and rewrite [cite:uuid]
  // tokens into markdown links so the whole message renders through a single
  // ReactMarkdown pass (splitting per-fragment previously broke paragraphs that
  // contained an inline citation into separate <p> blocks mid-sentence).
  const { processedContent, citationMap } = useMemo(() => {
    const safeCitations = citations || [];
    const map = new Map<string, { citation: Citation; index: number }>();
    safeCitations.forEach((c, i) => map.set(c.document_id, { citation: c, index: i }));

    let processed = mainContent.replace(
      /\[cite:([a-f0-9-]+)\]/gi,
      (_match, uuid) => `[CITE](#cite-${uuid})`
    );

    // Marca las normas nombradas (LIR, IVA, Código Tributario, "Ley N° …"); el renderer de abajo las pinta en negrita
    // y sin enlace (decisión del dueño, 14-10, como la maqueta). Se omite mientras se escribe para no partir un patrón.
    if (!isStreaming) {
      processed = linkifyNorms(processed);
    }

    return { processedContent: processed, citationMap: map };
  }, [mainContent, citations, isStreaming]);

  const renderedContent = useMemo(() => {
    if (!mainContent) return null;

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            if (href?.startsWith('#cite-')) {
              const uuid = href.slice('#cite-'.length);
              const entry = citationMap.get(uuid);
              if (!entry) return null;
              return (
                <CitationLink
                  citation={entry.citation}
                  index={entry.index}
                  onClick={(c) => onDocumentClick(c, citations ?? [])}
                />
              );
            }
            // Normas marcadas por linkifyNorms: negrita sin enlace (dueño, 14-10); el original se abre desde la cita.
            if (href?.includes('bcn.cl')) {
              return <strong className="font-semibold text-slate-900">{children}</strong>;
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800">
                {children}
              </a>
            );
          },
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    );
  }, [mainContent, processedContent, citationMap, onDocumentClick, citations]);

  const handleCopy = useCallback(() => {
    // Plain text with [cite:uuid] tokens turned into footnote numbers and a
    // "Fuentes:" block appended — see utils/exportText.ts.
    const plainText = buildAnswerPlainText({ content: mainContent || content, citations });
    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [mainContent, content, citations]);

  const handleDownloadDocx = useCallback(async () => {
    if (exportingDocx) return;
    setExportingDocx(true);
    try {
      // Dynamically imported so the docx library (and its dependencies) only
      // load into a separate chunk on first export click, not the main bundle.
      const { buildAnswerDocx, buildAnswerDocxFilename } = await import('../../utils/exportDocx');
      const blob = await buildAnswerDocx({ content: mainContent || content, citations, titulo: question });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildAnswerDocxFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // Silent fail: la exportación es opcional y no debe interrumpir la lectura.
    } finally {
      setExportingDocx(false);
    }
  }, [mainContent, content, citations, question, exportingDocx]);

  // USER bubble — compact right-aligned indigo bubble, kept as-is.
  if (role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 shadow-sm max-w-[80%]">
          <p className="text-sm leading-relaxed">{content}</p>
        </div>
      </div>
    );
  }

  const visibleSuggestions = suggestions.slice(0, MAX_SUGGESTIONS);

  // ASSISTANT message — rendered as a full-width document block, not a
  // chat bubble. No border/background/avatar; trust metadata (sources,
  // actions) lives at the edges, collapsed or hover-revealed.
  return (
    <div className="group mb-8">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-medium text-slate-400 uppercase tracking-wide">
        <Sparkles className="w-3 h-3" />
        AlertAI
      </div>

      {/* Typography: one font family (inherited app sans), one tight size
          set — body 15px, all heading levels collapse to the same two
          sizes (16px / 15px, both semibold) so nothing in a response reads
          as a different family or a disproportionate jump. */}
      <div className="text-[15px] leading-7 text-slate-700 prose prose-slate max-w-none font-sans prose-p:my-2.5 prose-headings:font-semibold prose-headings:font-sans prose-headings:text-slate-900 prose-h1:text-base prose-h1:mt-4 prose-h1:mb-1.5 prose-h2:text-base prose-h2:mt-4 prose-h2:mb-1.5 prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1.5 prose-h4:text-[15px] prose-h4:mt-3 prose-h4:mb-1 prose-h5:text-[15px] prose-h5:mt-3 prose-h5:mb-1 prose-h6:text-[15px] prose-h6:mt-3 prose-h6:mb-1 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-strong:text-slate-900 prose-blockquote:border-l-2 prose-blockquote:border-indigo-200 prose-blockquote:bg-slate-50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic prose-blockquote:font-sans prose-blockquote:text-slate-600 prose-blockquote:rounded-r prose-table:text-[15px] prose-th:bg-slate-50 prose-th:px-3 prose-th:py-1.5 prose-td:px-3 prose-td:py-1.5">
        {renderedContent}
      </div>

      {/* «Fuentes citadas» como nota al pie (M-8). Mientras se escribe, el progreso lo da la línea de estado. */}
      {!isStreaming && citations && citations.length > 0 && (
        <FuentesCitadas citations={citations} onOpen={(c) => onDocumentClick(c, citations)} />
      )}

      {/* Action row: copiar y exportar — reserved space, revealed on hover/focus only. */}
      {!isStreaming && id && (
        <div className="flex items-center gap-0.5 mt-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity motion-reduce:transition-none">
          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`p-1 rounded transition-colors ${
              copied ? 'text-green-500' : 'text-slate-400 hover:text-slate-600'
            }`}
            title={copied ? 'Copiado!' : 'Copiar respuesta'}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>

          {/* Download DOCX button */}
          <button
            onClick={handleDownloadDocx}
            disabled={exportingDocx}
            className="p-1 rounded transition-colors text-slate-400 hover:text-slate-600 disabled:opacity-60"
            title="Exportar DOCX"
          >
            {exportingDocx ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      )}

      {/* Clickable follow-up suggestions — capped at 3, muted, single row */}
      {!isStreaming && visibleSuggestions.length > 0 && onSuggestionClick && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {visibleSuggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => onSuggestionClick(s)}
              className="flex items-center gap-1 text-[11px] px-2.5 py-1 bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 text-slate-500 rounded-full border border-slate-200 transition-colors text-left"
            >
              <MessageSquare className="w-2.5 h-2.5 flex-shrink-0" />
              <span className="line-clamp-1">{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
